import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { valibotResolver } from '@hookform/resolvers/valibot';
import toast from 'react-hot-toast';
import { ManageOfferingsFormProps } from './types';
import { manageOfferingsSchema } from './schema';
import type { ManageOfferingsFormValues } from './schema';
import { styles } from './styles';
import { Modal, Button, Input } from '@v2/components';
import { DeleteConfirmationModal } from '@v2/components';
import {
  useCreateBitwerxOffering,
  useRemoveBitwerxOffering,
  useRemoveBitwerxSubOffering,
  useUpdateBitwerxOffering,
} from '@v2/hooks';
import type { CreateBitwerxOfferingPayload, UpdateBitwerxOfferingPayload } from '@v2/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { SubOfferingRow } from './components';

function getDefaultFormValues(
  offering: ManageOfferingsFormProps['offering'],
): ManageOfferingsFormValues {
  if (!offering) {
    return { offeringName: '', displayNames: {} };
  }
  const displayNames: Record<string, string> = {};
  (offering.subOfferings ?? []).forEach(sub => {
    displayNames[sub.id] = sub.displayName ?? sub.name ?? '';
  });
  return {
    offeringName: offering.name ?? '',
    displayNames,
  };
}

const ManageOfferingsForm: React.FC<ManageOfferingsFormProps> = ({
  offering,
  onClose,
  onCompleted,
  onCompletedDeleteOffering,
  onCompletedCreateOffering,
}) => {
  const isEditMode = offering != null;
  const defaultValues = useMemo(() => getDefaultFormValues(offering ?? null), [offering]);
  const { register, reset, getValues, watch, formState } = useForm<ManageOfferingsFormValues>({
    resolver: valibotResolver(manageOfferingsSchema),
    defaultValues,
  });
  const { isDirty } = formState;
  const watchedOfferingName = watch('offeringName');

  useEffect(() => {
    reset(getDefaultFormValues(offering ?? null));
  }, [offering, reset]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [addEntityName, setAddEntityName] = useState('');
  const [addDisplayName, setAddDisplayName] = useState('');
  const [newSubOfferings, setNewSubOfferings] = useState<
    Array<{ tempId: string; entityName: string; displayName: string }>
  >([]);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [pendingDeleteSubOfferingId, setPendingDeleteSubOfferingId] = useState<string | null>(null);

  const { createBitwerxOffering, loading: createOfferingLoading } = useCreateBitwerxOffering();
  const { deleteBitwerxOffering, loading: deleteOfferingLoading } = useRemoveBitwerxOffering();
  const {
    removeBitwerxSubOffering,
    loading: removeSubOfferingLoading,
    error: removeSubOfferingError,
  } = useRemoveBitwerxSubOffering();
  const {
    updateBitwerxOffering,
    loading: updateOfferingLoading,
    error: updateOfferingError,
  } = useUpdateBitwerxOffering();

  const existingSubOfferings = offering?.subOfferings ?? [];

  const handleConfirmRemoveSubOffering = async () => {
    if (!pendingDeleteSubOfferingId) return;
    const result = await removeBitwerxSubOffering(pendingDeleteSubOfferingId);
    setPendingDeleteSubOfferingId(null);
    if (result !== null) {
      toast.success('Sub offering removed');
      onCompleted?.();
    } else {
      toast.error(removeSubOfferingError ?? 'Failed to remove sub offering');
    }
  };

  const handleAddSubOffering = () => {
    const entity = addEntityName.trim();
    const display = addDisplayName.trim();
    if (!entity) return;
    setNewSubOfferings(prev => [
      ...prev,
      {
        tempId: `new-${Date.now()}-${prev.length}`,
        entityName: entity,
        displayName: display || entity,
      },
    ]);
    setAddEntityName('');
    setAddDisplayName('');
    setShowAddForm(false);
  };

  const handleRemoveNewSubOffering = (tempId: string) => {
    setNewSubOfferings(prev => prev.filter(n => n.tempId !== tempId));
  };

  const handleClear = () => {
    reset(defaultValues);
    setNewSubOfferings([]);
    setAddEntityName('');
    setAddDisplayName('');
    setShowAddForm(false);
  };

  const handleSubmitEdit = async () => {
    if (!offering) return;
    const values = getValues();
    const offeringName = values.offeringName;
    const displayNames = values.displayNames ?? {};

    const nameUnchanged = offeringName === (offering.name ?? '');
    const name = nameUnchanged ? '' : offeringName;
    const subOfferingsPayload = [
      ...existingSubOfferings.map(sub => ({
        name: sub.name,
        displayName: (displayNames[sub.id] ?? '').trim() || (sub.displayName ?? sub.name ?? ''),
        addition: false,
      })),
      ...newSubOfferings.map(({ entityName, displayName }) => ({
        name: entityName,
        displayName: displayName || entityName,
        addition: true,
      })),
    ];

    const payload: UpdateBitwerxOfferingPayload = {
      displayId: 0,
      name,
      ...(nameUnchanged ? {} : { newName: offeringName }),
      subOfferings: subOfferingsPayload,
      id: offering.id,
      createdById: offering.createdById,
      createdDate: offering.createdDate,
      createdByFirstName: offering.createdByFirstName,
      createdByLastName: offering.createdByLastName,
      modifiedById: offering.modifiedById,
      modifiedDate: offering.modifiedDate,
    };

    const result = await updateBitwerxOffering(offering.id, payload);
    if (result !== null) {
      toast.success('Sub offerings updated successfully');
      setNewSubOfferings([]);
      reset(defaultValues);
      onCompleted?.();
    } else {
      toast.error(updateOfferingError ?? 'Failed to update Bitwerx offering');
    }
  };

  const handleSubmitCreate = async () => {
    const values = getValues();
    const offeringName = values.offeringName;
    if (offeringName == null || offeringName === '') return;

    const createPayload: CreateBitwerxOfferingPayload = {
      name: offeringName,
      newName: offeringName,
    };
    if (newSubOfferings.length > 0) {
      createPayload.subOfferings = newSubOfferings.map(({ entityName, displayName }) => ({
        name: entityName,
        displayName: displayName || entityName,
        addition: true as const,
      }));
    }
    const result = await createBitwerxOffering(createPayload);
    if (result !== null) {
      toast.success('Offering created successfully');
      onCompletedCreateOffering?.(result as string);
    } else {
      toast.error('Failed to create Bitwerx offering');
    }
  };

  const handleSubmitChanges = async () => {
    if (isEditMode && offering) {
      await handleSubmitEdit();
    } else {
      await handleSubmitCreate();
    }
  };

  const handleDeleteOfferingClick = () => {
    setIsConfirmDeleteOpen(true);
  };

  const handleConfirmDeleteOffering = async () => {
    if (!offering) return;
    const result = await deleteBitwerxOffering(offering.id);
    if (result !== null) {
      toast.success('Offering deleted successfully');
      setIsConfirmDeleteOpen(false);
      onCompletedDeleteOffering?.();
    } else {
      toast.error('Failed to delete Bitwerx offering');
    }
  };

  const offeringName = isEditMode ? getValues('offeringName') : (watchedOfferingName ?? '');
  const hasPendingChanges = isEditMode
    ? isDirty || newSubOfferings.length > 0
    : offeringName.trim() !== '' || newSubOfferings.length > 0;

  const submitLoading = isEditMode ? updateOfferingLoading : createOfferingLoading;
  const submitDisabled = isEditMode
    ? !hasPendingChanges || updateOfferingLoading
    : !offeringName.trim() || createOfferingLoading;

  const modalTitle = isEditMode ? 'Manage Sub Offerings' : 'Create Offering';

  return (
    <>
      <Modal
        isOpen={true}
        onClose={onClose}
        title={modalTitle}
        size="50vw"
        variant="side"
        footer={
          <>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleClear}
              disabled={!hasPendingChanges}
              className={styles.clearButton}>
              Clear
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitChanges}
              disabled={submitDisabled}
              isLoading={submitLoading}
              className={styles.submitButton}>
              {isEditMode ? 'Save Changes' : 'Create'}
            </Button>
          </>
        }>
        <div className={styles.formContainer}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Offering name</div>
            <div className={styles.offeringRow}>
              <div className={styles.offeringInput}>
                <Input
                  {...register('offeringName')}
                  placeholder="Offering name"
                  autoComplete="off"
                />
              </div>
              {isEditMode && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleDeleteOfferingClick}
                  icon={<FontAwesomeIcon icon={faTrash} />}
                  aria-label="Delete offering"
                />
              )}
            </div>
          </div>

          <div className={styles.subOfferingsSection}>
            <div className={styles.subOfferingsHeader}>
              <span className={styles.sectionTitle}>Sub offerings</span>
              {!showAddForm ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowAddForm(true)}
                  icon={<FontAwesomeIcon icon={faPlus} />}>
                  Add Sub Offering
                </Button>
              ) : null}
            </div>
            <div className={styles.listContainer}>
              {existingSubOfferings.length === 0 && newSubOfferings.length === 0 ? (
                <div className={styles.emptyList}>No sub offerings yet.</div>
              ) : (
                <ul className={styles.list}>
                  {existingSubOfferings.map(sub => (
                    <SubOfferingRow
                      key={sub.id}
                      entityName={sub.name}
                      displayNameInput={
                        <Input
                          {...register(`displayNames.${sub.id}`)}
                          label="Display name"
                          placeholder="Display name"
                          autoComplete="off"
                        />
                      }
                      onDelete={() => setPendingDeleteSubOfferingId(sub.id)}
                      deleteAriaLabel="Delete sub offering"
                      deleteDisabled={removeSubOfferingLoading}
                    />
                  ))}
                  {newSubOfferings.map(newSub => (
                    <SubOfferingRow
                      key={newSub.tempId}
                      entityName={newSub.entityName}
                      displayNameInput={
                        <Input
                          label="Display name"
                          value={newSub.displayName}
                          onChange={e => {
                            const value = e.target.value;
                            setNewSubOfferings(prev =>
                              prev.map(n =>
                                n.tempId === newSub.tempId ? { ...n, displayName: value } : n,
                              ),
                            );
                          }}
                          placeholder="Display name"
                          autoComplete="off"
                        />
                      }
                      onDelete={() => handleRemoveNewSubOffering(newSub.tempId)}
                      deleteAriaLabel="Remove sub offering"
                    />
                  ))}
                </ul>
              )}
            </div>

            {showAddForm ? (
              <div className={styles.addForm}>
                <div className={styles.addFormRow}>
                  <div className={styles.addFormField}>
                    <Input
                      label="Entity Name"
                      value={addEntityName}
                      onChange={e => {
                        const value = e.target.value;
                        setAddEntityName(value);
                      }}
                      placeholder="Entity name"
                      autoComplete="off"
                    />
                  </div>
                  <div className={styles.addFormField}>
                    <Input
                      label="Display Name"
                      value={addDisplayName}
                      onChange={e => {
                        const value = e.target.value;
                        setAddDisplayName(value);
                      }}
                      placeholder="Display name"
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div className={styles.addFormActions}>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAddSubOffering}
                    disabled={!addEntityName.trim()}>
                    Add
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowAddForm(false);
                      setAddEntityName('');
                      setAddDisplayName('');
                    }}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </Modal>

      <DeleteConfirmationModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        onConfirm={handleConfirmDeleteOffering}
        isDeleting={deleteOfferingLoading}
        title="Delete Bitwerx Offering"
        message={`Are you sure you want to delete Bitwerx Offering: ${offering?.name}?`}
      />

      <DeleteConfirmationModal
        isOpen={pendingDeleteSubOfferingId !== null}
        onClose={() => setPendingDeleteSubOfferingId(null)}
        onConfirm={handleConfirmRemoveSubOffering}
        isDeleting={removeSubOfferingLoading}
        title="Delete Sub Offering"
        message="Are you sure you want to delete this sub offering?"
      />
    </>
  );
};

export default ManageOfferingsForm;
